import { appManager, AppState } from '../manager';
import * as types from '../players/types';
import { arrayToIndexedObject, IndexedObject } from 'services/indexedObject';
import { Wallet } from '../players/types';
import { OptionsChainRow } from '../../../views/TraineeSession/OptionsTab/OptionsChain';
import {
  OptionsContractType,
  PartialOptionsInstrument,
  PlayerOptionsContractType,
} from './types';

export const fetchOptionsChains = appManager.createApi<
  {
    session_id: number;
  },
  Record<
    number,
    {
      contracts: OptionsContractType[];
      underlying_spot: number;
      expiration_dates: string[];
    }
  >,
  AppState
>('FETCH_OPTIONS_CHAINS', {
  path: '/sessions/:session_id/fetch_options_chains',
  method: 'GET',
  startReducer(state) {
    state.sessions.items[state.active_session].options_chains_status = {
      waiting: true,
      fetched: false,
    };
  },
  successReducer(state, result) {
    Object.entries(result).forEach(([assetIdStr, data]) => {
      const assetId = Number(assetIdStr);
      state.underlying_assets.items[assetId].options_contracts = data.contracts;
      state.underlying_assets.items[assetId].underlying_spot =
        data.underlying_spot;
      state.underlying_assets.items[assetId].expiration_dates =
        data.expiration_dates;
    });
  },
  failReducer(state) {
    // ...
  },
});

export const fetchOptionsChainsNew = appManager.createApi<
  {
    session_id: number;
  },
  Record<
    number,
    {
      contracts: OptionsContractType[];
      underlying_spot: number;
      expiration_dates: string[];
    }
  >,
  AppState
>('FETCH_OPTIONS_CHAINS_NEW', {
  path: '/sessions/:session_id/fetch_options_chains',
  method: 'GET',
  startReducer(state) {
    state.options_contracts_data.fetching = true;
    state.options_contracts_data.fetched = false;
  },
  successReducer(state, result) {
    Object.entries(result).forEach(([assetIdStr, data]) => {
      const assetId = Number(assetIdStr);
      state.options_contracts_data.of_asset[assetId] = {
        contracts: data.contracts,
        underlying_spot: data.underlying_spot,
        expiration_dates: data.expiration_dates,
      };
    });
    state.options_contracts_data.fetching = false;
    state.options_contracts_data.fetched = true;
  },
  failReducer(state) {
    state.options_contracts_data.fetching = false;
    state.options_contracts_data.fetched = false;
  },
});

export const fetchOptionsContracts = appManager.createApi<
  {
    player_id: number;
  },
  // types.PlayerAllocationsByInvestmentProfile[],
  PlayerOptionsContractType[],
  AppState
  // >('UPDATE_PLAYER_GROUP_ALLOCATIONS', {
>('FETCH_PLAYER_OPTIONS_CONTRACTS', {
  method: 'POST',
  path: '/players/:player_id/get_options_contracts',
  successReducer(state, result) {
    state.player.item.player_options_contracts = arrayToIndexedObject(result);
    // const player = state.player.item;
    // player.strategic_group_allocations = result;
    // result received on socket so trainer can receive the update as well
  },
});

export const fetchPlayerOptionsInstruments = appManager.createApi<
  {
    // even though it looks like the player is not targeted, it is. check backend
    session_id: number;
  },
  {
    id: number;
    options_asset: number;
    options_contracts: PlayerOptionsContractType[];
  }[],
  AppState
>('FETCH_PLAYER_OPTIONS_INSTRUMENTS', {
  method: 'POST',
  path: '/players/get_options_instruments',
  startReducer(state) {
    state.player.item.options_instruments.fetched = false;
    state.player.item.options_instruments.fetching = true;
  },
  successReducer(state, result) {
    // For each instrument, convert its options_contracts to an IndexedObject before indexing instruments
    const instruments = result.map(inst => ({
      ...inst,
      options_contracts: arrayToIndexedObject(inst.options_contracts),
    }));
    state.player.item.options_instruments.items =
      arrayToIndexedObject(instruments);

    state.player.item.options_instruments.fetched = true;
    state.player.item.options_instruments.fetching = false;
  },
  failReducer(state) {
    state.player.item.options_instruments.fetched = false;
    state.player.item.options_instruments.fetching = false;
  },
});

appManager.createSocketListener<
  PartialOptionsInstrument[],
  // unknown,
  // UpdateFins[],
  AppState
>('update_options_contracts_price', (state, result) => {
  result.forEach(instrument => {
    instrument.options_contracts.forEach(new_contract => {
      const contract =
        state.player.item.options_instruments.items[instrument.id]
          .options_contracts[new_contract.id];
      if (contract) {
        contract.current_bid = new_contract.current_bid;
        contract.current_ask = new_contract.current_ask;
        contract.current_iv = new_contract.current_iv;
        console.log('new_contract:', new_contract);
        console.log('new_contract.current_delta:', new_contract.current_delta);
        contract.current_delta = new_contract.current_delta;
      }
    });
  });
});

export const earlyCloseOptionsContract = appManager.createApi<
  {
    player_id: number;
    contract_id: number;
    options_instrument_id: number;
  },
  // types.PlayerAllocationsByInvestmentProfile[],
  {
    contract_data: PlayerOptionsContractType;
    wallet_data: Wallet;
  },
  AppState
>('EARLY_CLOSE_OPTIONS_CONTRACT', {
  method: 'POST',
  path: '/players/:player_id/early_close_options_contract',
  successReducer(state, result, payload) {
    state.player.item.options_instruments.items[
      payload.options_instrument_id
    ].options_contracts[payload.contract_id] = result.contract_data;
    const wallet_id = state.player.item.wallets.findIndex(
      // would be nice if wallets were indexed
      wallet => wallet.id === result.wallet_data.id
    );
    if (wallet_id !== -1) {
      state.player.item.wallets[wallet_id] = result.wallet_data;
    }
  },
});

appManager.createSocketListener<
  {
    contract_data: PlayerOptionsContractType;
    wallet_data: Wallet;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('options_contract_closed', (state, result) => {
  state.player.item.options_instruments.items[
    result.contract_data.options_instrument
  ].options_contracts[result.contract_data.id] = result.contract_data;
  const wallet_id = state.player.item.wallets.findIndex(
    // would be nice if wallets were indexed
    wallet => wallet.id === result.wallet_data.id
  );
  if (wallet_id !== -1) {
    state.player.item.wallets[wallet_id] = result.wallet_data;
  }
});

export const earlyExerciseOptionsContract = appManager.createApi<
  {
    player_id: number;
    contract_id: number;
    options_instrument_id: number;
  },
  // types.PlayerAllocationsByInvestmentProfile[],
  {
    contract_data: PlayerOptionsContractType;
    wallet_data: Wallet;
  },
  AppState
>('EARLY_EXERCISE_OPTIONS_CONTRACT', {
  method: 'POST',
  path: '/players/:player_id/early_exercise_options_contract',
  successReducer(state, result, payload) {
    // state.player.item.options_instruments[
    //   payload.options_instrument_id
    // ].options_contracts[payload.contract_id] = result.contract_data;
    // const wallet_id = state.player.item.wallets.findIndex(
    //   // would be nice if wallets were indexed
    //   wallet => wallet.id === result.wallet_data.id
    // );
    // if (wallet_id !== -1) {
    //   state.player.item.wallets[wallet_id] = result.wallet_data;
    // }
  },
});

appManager.createSocketListener<
  PlayerOptionsContractType,
  // unknown,
  // UpdateFins[],
  AppState
>('options_contract_execised_early', (state, result) => {
  state.player.item.options_instruments.items[
    result.options_instrument
  ].options_contracts[result.id] = result;
});

appManager.createSocketListener<
  PlayerOptionsContractType[],
  // unknown,
  // UpdateFins[],
  AppState
>('expiring_contracts_update', (state, result) => {
  result.forEach(contract => {
    const options_instrument_id = contract.options_instrument;
    state.player.item.options_instruments.items[
      options_instrument_id
    ].options_contracts[contract.id] = contract;
  });
});

export const enterOptionsContract = appManager.createApi<
  {
    player_id: number;
    contract: OptionsContractType;
    row: OptionsChainRow;
    type: number;
    txn: number;
    contracts: number;
    options_asset: number;
  },
  // types.PlayerAllocationsByInvestmentProfile[],
  {
    updated_wallet: Wallet;
    new_contract: PlayerOptionsContractType;
  },
  AppState
  // >('UPDATE_PLAYER_GROUP_ALLOCATIONS', {
>('ENTER_OPTIONS_CONTRACT', {
  method: 'POST',
  path: '/players/:player_id/enter_options_contract',
  successReducer(state, result) {
    const wallet = state.player.item.wallets.find(
      item => item.id === result.updated_wallet.id
    );
    if (wallet) {
      wallet.amount = result.updated_wallet.amount;
      wallet.collateral_amount = result.updated_wallet.collateral_amount;
      wallet.client_money = result.updated_wallet.client_money;
      wallet.client_interest = result.updated_wallet.client_interest;
      wallet.lombard_interest = result.updated_wallet.lombard_interest;
      wallet.options_net_premiums = result.updated_wallet.options_net_premiums;
    }

    const instId = result.new_contract.options_instrument;
    const instrumentEntry = state.player.item.options_instruments.items[instId];
    const contracts =
      instrumentEntry.options_contracts as IndexedObject<PlayerOptionsContractType>;
    const cid = result.new_contract.id;
    if (!contracts[cid]) {
      contracts[cid] = result.new_contract;
    }
  },
});

appManager.createSocketListener<
  {
    new_contract: PlayerOptionsContractType;
  },
  // unknown,
  // UpdateFins[],
  AppState
>('player_entered_new_options_contract', (state, result) => {
  // const contracts =
  //   state.player.item.options_instruments[
  //     result.new_contract.options_instrument
  //   ].options_contracts;
  // const exists = contracts.some(c => c.id === result.new_contract.id);
  // if (!exists) {
  //   state.player.item.options_instruments[
  //     result.new_contract.options_instrument
  //   ].options_contracts.push(result.new_contract);
  // }
  const instId = result.new_contract.options_instrument;
  const instrumentEntry = state.player.item.options_instruments.items[instId];
  const contracts =
    instrumentEntry.options_contracts as IndexedObject<PlayerOptionsContractType>;
  const cid = result.new_contract.id;
  if (!contracts[cid]) {
    contracts[cid] = result.new_contract;
  }
});

appManager.createSocketListener<
  Record<
    number,
    {
      contracts: OptionsContractType[];
      underlying_spot: number;
      expiration_dates: string[];
    }
  >,
  AppState
>('send_new_period_options_chains', (state, result) => {
  console.log('send_new_period_options_chains: ', result);

  // Object.entries(result).forEach(([assetIdStr, data]) => {
  //   const assetId = Number(assetIdStr);
  //   state.underlying_assets.items[assetId].options_contracts = data.contracts;
  //   state.underlying_assets.items[assetId].underlying_spot =
  //     data.underlying_spot;
  //   state.underlying_assets.items[assetId].expiration_dates =
  //     data.expiration_dates;
  // });

  Object.entries(result).forEach(([assetIdStr, data]) => {
    const assetId = Number(assetIdStr);
    state.options_contracts_data.of_asset[assetId] = {
      contracts: data.contracts,
      underlying_spot: data.underlying_spot,
      expiration_dates: data.expiration_dates,
    };
  });

  const selectedOptionsContract = state.player.item.selectedOptionsContract;
  if (selectedOptionsContract) {
    // TODO: do this -V, currently defaulting to ATM each update  (by setting to undefined, then OptionsChain sets to ATM)
    // use previous selection's strike and expiration to find matching contract from new batch that just came and
    // update selectedOptionsContract. If new data batch doesn't provide an exact match for strike and expiration
    // default to selecting ATM
  }
  state.player.item.selectedOptionsContract = undefined;
});

export const setSelectedOptionsContract = appManager.createLocalEvent(
  'set_selected_options_contract',
  (state, payload) => {
    state.player.item.selectedOptionsContract = payload.selectedOptionsContract;
  }
);
